import React, { useState } from "react";
import { ScrollReveal } from "features/whop/whopLanding/components/animations.tsx";
import styled from "styled-components";
import { Role } from "features/whop/whopLanding/WhopLandingPage.tsx";
import {
  CalendarIcon,
  CopyIcon,
} from "features/whop/whopLanding/components/shared.tsx";
import { copyCodeToClipboard } from "utils/misc";
import { memberMessage } from "features/whop/whopLanding/components/MessageSection.tsx";
import { finalCtaContent as content } from "features/whop/whopLanding/content.ts";

interface FinalCTAProps {
  role: Role;
}

export const FinalCTA: React.FC<FinalCTAProps> = ({ role }) => {
  const text = content[role];
  const [copied, setCopied] = useState(false);

  const handleCopyMessage = () => {
    copyCodeToClipboard(memberMessage).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Section>
      <Inner>
        <ScrollReveal>
          <GradientBox>
            <Headline>{text.headline}</Headline>
            <Subheadline>{text.subheadline}</Subheadline>
            <ButtonGroup>
              {text.primaryCTA && (
                <PrimaryButton
                  onClick={
                    text.primaryIcon === "copy" ? handleCopyMessage : undefined
                  }
                >
                  {text.primaryIcon === "copy" ? <CopyIcon /> : <SendIcon />}
                  {text.primaryIcon === "copy" && copied
                    ? "Copied to Clipboard!"
                    : text.primaryCTA}
                </PrimaryButton>
              )}
              {text.secondaryCTA && (
                <SecondaryButton
                  onClick={() =>
                    window.open(
                      "https://calendly.com/cornix-trading/cornix-channel-onboarding",
                      "_blank"
                    )
                  }
                >
                  <CalendarIcon />
                  {text.secondaryCTA}
                </SecondaryButton>
              )}
            </ButtonGroup>
            <TrustNote>
              Join 100+ trading groups already using automated execution
            </TrustNote>
          </GradientBox>
        </ScrollReveal>
      </Inner>
    </Section>
  );
};

const SendIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

const Section = styled.section`
  padding: 80px 24px;
  background: ${(p) => (p.theme.isDarkMode ? "#0a0a0a" : "#ffffff")};
  @media (max-width: 768px) {
    padding: 56px 20px;
  }
  @media (max-width: 480px) {
    padding: 40px 16px;
  }
`;

const Inner = styled.div`
  max-width: 960px;
  margin: 0 auto;
`;

const GradientBox = styled.div`
  position: relative;
  border-radius: 24px;
  padding: 64px 40px;
  overflow: hidden;
  background: linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 45%, #2563eb 100%);
  box-shadow: 0 24px 80px rgba(37, 99, 235, 0.35);
  text-align: center;
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(
      ellipse 70% 60% at 50% 0%,
      rgba(255, 255, 255, 0.08) 0%,
      transparent 70%
    );
    pointer-events: none;
  }
  @media (max-width: 768px) {
    padding: 48px 32px;
    border-radius: 16px;
  }
  @media (max-width: 480px) {
    padding: 40px 24px;
    border-radius: 12px;
  }
`;

const Headline = styled.h2`
  font-size: clamp(1.5rem, 5vw, 4rem);
  font-weight: 700;
  color: #fff;
  line-height: 1.1;
  letter-spacing: -0.02em;
  max-width: 800px;
  margin: 0 auto 20px;
`;

const Subheadline = styled.p`
  font-size: clamp(1rem, 2.5vw, 1.875rem);
  color: rgba(219, 234, 254, 1);
  max-width: 600px;
  margin: 0 auto 32px;
  line-height: 1.5;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  justify-content: center;
  align-items: center;
  margin-bottom: 24px;
  @media (min-width: 480px) {
    flex-direction: row;
  }
`;

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  white-space: nowrap;
  padding: 14px 28px;
  background: #fff;
  color: #2563eb;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 12px;
  border: none;
  cursor: pointer;
  width: 100%;
  max-width: 320px;
  @media (min-width: 480px) {
    width: auto;
    min-width: 280px;
    padding: 20px 0;
    font-size: 1.125rem;
  }
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  transition: background 0.2s, box-shadow 0.2s, transform 0.2s;
  &:hover {
    background: #eff6ff;
    box-shadow: 0 20px 60px rgba(255, 255, 255, 0.3);
    transform: scale(1.02);
  }
`;

const SecondaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  white-space: nowrap;
  padding: 14px 28px;
  background: transparent;
  color: #fff;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 12px;
  border: 2px solid rgba(255, 255, 255, 0.8);
  cursor: pointer;
  width: 100%;
  max-width: 320px;
  @media (min-width: 480px) {
    width: auto;
    min-width: 280px;
    padding: 20px 8px;
    font-size: 1.125rem;
  }
  transition: background 0.2s, border-color 0.2s;
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: #fff;
  }
`;

const TrustNote = styled.p`
  color: rgba(191, 219, 254, 1);
  font-size: 1rem;
`;
